import { useCallback, useEffect, useRef, useState } from 'react';

import { useConfluenceAnalyticsEvents } from '@confluence/analytics/entry-points/useConfluenceAnalyticsEvents.ts';

import { appendNewLinks } from '../utils/appendNewLinks.ts';
import { reportUnexpectedGraphPipelineError } from '../utils/graphErrorReporting.ts';
import { readIncomingLinkUrls } from '../utils/readIncomingLinkUrls.ts';
import type { EnhancedSmartLink } from '../utils/smartLinkTypes.ts';

import type { useIncomingLinksQuery$data } from './__generated__/useIncomingLinksQuery.graphql.ts';
import { createIncomingLinksPageObservation } from './incomingLinksPageObservation.ts';
import type { QueryLoadingDurationAttributes } from './useQueryLoadingDuration.ts';
import {
	fetchIncomingLinksPage,
	type IncomingLinksPageFetchMore,
} from './fetchIncomingLinksPage.ts';
import {
	type IncomingLinksPagePhase,
	getIncomingLinksLoadMoreSucceededEvent,
	getIncomingLinksSucceededEvent,
} from './incomingLinksAnalytics.ts';

const INITIAL_RESOLVED_LINK_TARGET = 3;

export type UseIncomingLinksGraphPagesOptions = {
	data: useIncomingLinksQuery$data | undefined;
	queryLoading: boolean;
	fetchMore: IncomingLinksPageFetchMore;
	skip: boolean;
	loadMorePageSize: number;
	hydrateLinks: (urls: string[]) => Promise<EnhancedSmartLink[]>;
	readInitialQueryDuration?: () => QueryLoadingDurationAttributes;
};

export type UseIncomingLinksGraphPagesResult = {
	links: EnhancedSmartLink[];
	loading: boolean;
	settled: boolean;
	loadingMore: boolean;
	hasNextPage: boolean;
	endCursor: string | null;
	loadMore: () => void;
};

const readPageInfo = (
	data: useIncomingLinksQuery$data | undefined,
): { hasNextPage: boolean; endCursor: string | null } => {
	const pageInfo = data?.graphStore_cypherQueryV2?.pageInfo;
	return {
		hasNextPage: pageInfo?.hasNextPage ?? false,
		endCursor: pageInfo?.endCursor ?? null,
	};
};

export const useIncomingLinksGraphPages = ({
	data,
	queryLoading,
	fetchMore,
	skip,
	loadMorePageSize,
	hydrateLinks,
	readInitialQueryDuration,
}: UseIncomingLinksGraphPagesOptions): UseIncomingLinksGraphPagesResult => {
	const { createEvent } = useConfluenceAnalyticsEvents();
	const hasFiredRef = useRef(false);
	const pageObservationRef = useRef(createIncomingLinksPageObservation());
	// Dedupes across pages so re-renders and merged-store updates never re-resolve a url.
	const knownUrlsRef = useRef<Set<string>>(new Set());
	const initialResolvedRef = useRef(false);
	const [graphLinks, setGraphLinks] = useState<EnhancedSmartLink[]>([]);
	const [initialResolved, setInitialResolved] = useState(false);
	const [resolving, setResolving] = useState(false);
	const [loadingMore, setLoadingMore] = useState(false);
	const { hasNextPage, endCursor } = readPageInfo(data);

	const fetchNextPage = useCallback(
		(after: string, phase: IncomingLinksPagePhase) =>
			fetchIncomingLinksPage({
				fetchMore,
				createEvent,
				pageSize: loadMorePageSize,
				after,
				phase,
				observation: pageObservationRef.current,
			}),
		[createEvent, fetchMore, loadMorePageSize],
	);

	// Hydrate only the urls not seen before and append them in order. `knownUrlsRef`
	// is advanced only after a hydration commits, so a cancelled attempt (e.g.
	// StrictMode double-invoke) leaves those urls for the surviving attempt.
	const hydrateAndAppendPage = useCallback(
		async (urls: string[], isCancelled: () => boolean): Promise<number> => {
			const newUrls = urls.filter((url) => !knownUrlsRef.current.has(url));
			if (newUrls.length === 0) {
				return 0;
			}

			const hydrated = await hydrateLinks(newUrls);
			if (isCancelled()) {
				return 0;
			}

			newUrls.forEach((url) => knownUrlsRef.current.add(url));
			setGraphLinks((prev) => appendNewLinks(prev, hydrated));
			return hydrated.length;
		},
		[hydrateLinks],
	);

	// Hydrate the initial page once the query settles. We deliberately do NOT gate on
	// `error`: with `errorPolicy: 'all'` a partial cross-activation error still returns
	// usable rows, and bailing here would drop them (see the query's `errorPolicy`
	// note) — the owning query hook reports returned errors. `initialResolvedRef` stops
	// loadMore's merged-store updates from re-running this initial hydration.
	useEffect(() => {
		if (skip || queryLoading || !data || initialResolvedRef.current) {
			return;
		}

		let cancelled = false;
		const queryDuration = readInitialQueryDuration?.() ?? {};
		setResolving(true);

		// When the first graph page hydrates to 0 displayable links (common when
		// page-1 rows are uncatalogued or unresolvable), keep paging until we have
		// enough links to render the initial section or pagination is exhausted.
		const hydrateInitialPages = async () => {
			let resolvedCount = 0;
			let currentData: useIncomingLinksQuery$data = data;

			while (!cancelled) {
				resolvedCount += await hydrateAndAppendPage(
					readIncomingLinkUrls(currentData),
					() => cancelled,
				);
				const currentPageInfo = readPageInfo(currentData);

				if (cancelled || resolvedCount >= INITIAL_RESOLVED_LINK_TARGET) {
					break;
				}

				if (!currentPageInfo.hasNextPage || !currentPageInfo.endCursor) {
					break;
				}

				const nextPageResult = await fetchNextPage(currentPageInfo.endCursor, 'initialCatchUp');

				currentData = nextPageResult.data;
			}

			return resolvedCount;
		};

		hydrateInitialPages()
			.then((count) => {
				if (cancelled) {
					return;
				}
				initialResolvedRef.current = true;
				setInitialResolved(true);
				setResolving(false);
				if (!hasFiredRef.current) {
					hasFiredRef.current = true;
					createEvent(getIncomingLinksSucceededEvent({ count, ...queryDuration })).fire();
				}
			})
			.catch((err: unknown) => {
				// hydrateIncomingLinks/resolveSmartLinkByUrl swallow and report per-url
				// failures themselves; report unexpected pipeline rejections here and avoid
				// leaving `resolving` stuck. Mark the initial attempt resolved (even though it
				// failed) so the `initialResolutionPending` loading window closes and the effect
				// does not retry.
				if (!cancelled) {
					reportUnexpectedGraphPipelineError(err);
					initialResolvedRef.current = true;
					setInitialResolved(true);
					setResolving(false);
				}
			});

		return () => {
			cancelled = true;
		};
	}, [
		skip,
		queryLoading,
		data,
		fetchNextPage,
		hydrateAndAppendPage,
		createEvent,
		readInitialQueryDuration,
	]);

	const loadMore = useCallback(() => {
		// `initialResolvedRef` gates against a click landing before the first page's
		// urls are committed to `knownUrlsRef`, which would re-resolve page 1 and
		// duplicate its links.
		if (skip || loadingMore || !hasNextPage || !endCursor || !initialResolvedRef.current) {
			return;
		}

		setLoadingMore(true);
		fetchNextPage(endCursor, 'loadMore')
			.then((result) =>
				hydrateAndAppendPage(readIncomingLinkUrls(result.data), () => false).then((count) => {
					createEvent(getIncomingLinksLoadMoreSucceededEvent({ count })).fire();
				}),
			)
			// fetchNextPage reports its own outcome; preserve the existing retry and display behavior.
			.catch(() => {})
			.finally(() => setLoadingMore(false));
	}, [skip, loadingMore, hasNextPage, endCursor, fetchNextPage, hydrateAndAppendPage, createEvent]);

	// Stay `loading` from query start through the first hydration commit. `queryLoading || resolving`
	// alone leaves a gap: after the query settles (`queryLoading` → false) but before the hydration
	// effect sets `resolving`, one render commits with `loading` false and `graphLinks` still empty. An
	// inbound-only byline returns `null` on that render, so it flashes out and back in before the
	// resolved trigger appears. Deriving the pending window from `data`/`initialResolved` (both
	// available during render, no effect latency) closes the gap. CCINT-8932
	// Keep completion in state as well as the guard ref: an empty/cached page can resolve before
	// React commits `resolving: true`, and automatic batching can otherwise collapse the true/false
	// updates without producing the render that observes the updated ref. CCINT-9096
	const initialResolutionPending = !skip && data != null && !initialResolved;

	return {
		links: graphLinks,
		loading: queryLoading || resolving || initialResolutionPending,
		settled: !skip && !queryLoading && (initialResolved || data == null),
		loadingMore,
		hasNextPage,
		endCursor,
		loadMore,
	};
};
